import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, TextField, MenuItem } from '@mui/material';
import { useGetStudentEnrollments } from '../../hooks/studentEnrollments/useGetStudentEnrollments';
import { useDeleteStudentEnrollment } from '../../hooks/studentEnrollments/useDeleteStudentEnrollment';
import { useGetClasses } from '../../hooks/classes/useGetClasses';
import { useGetLevels } from '../../hooks/levels/useGetLevels';
import { useCurrentContext } from '../../hooks/useCurrentContext';
import { usePermissions } from '../../auth/PermissionsContext';
import useTableActions from '../../hooks/common/useTableActions';
import TableLayout from '../global/Table/TableLayout';
import StudentEnrollmentsTableColumns from './Table/StudentEnrollmentsTableColumns';
import StudentEnrollmentsTableRow from './Table/StudentEnrollmentsTableRow';

const rtlSelectSx = (isArabic) => isArabic ? {
    '& .MuiInputLabel-root': { left: 'unset', right: 32, transformOrigin: 'top right' },
    '& .MuiInputLabel-shrink': { right: 32 },
    '& .MuiSelect-icon': { left: 7, right: 'unset' },
    '& .MuiSelect-select': { textAlign: 'right', paddingRight: '14px !important', paddingLeft: '32px !important' },
    '& .MuiOutlinedInput-notchedOutline legend': { textAlign: 'right' },
} : {};

const StudentEnrollmentsTable = () => {
    const { i18n } = useTranslation();
    const isArabic = i18n.language === 'ar';
    const navigate = useNavigate();
    const { has } = usePermissions();

    const { currentTermId } = useCurrentContext();
    const [filterClassId, setFilterClassId] = useState('');
    const [filterLevelId, setFilterLevelId] = useState('');
    const [filterStatus, setFilterStatus] = useState('');

    const perms = useMemo(() => ({
        list: has('student_enrollments_list'), create: has('student_enrollments_create'),
        edit: has('student_enrollments_edit'), delete: has('student_enrollments_delete'),
    }), [has]);

    const { deleteEnrollment, isDelete } = useDeleteStudentEnrollment();
    const { classes } = useGetClasses(1, 999);
    const { levels } = useGetLevels(1, 999);

    const {
        page, perPage, search, sortBy, sortDir, menuAnchorEl,
        setSearch, handleRequestSort,
        handleMenuOpen, handleMenuClose, handleEdit, handleMenuDelete,
        handlePageChange, handlePerPageChange,
    } = useTableActions({ basePath: '/student-enrollments', deleteSingle: deleteEnrollment });

    const filters = useMemo(() => ({
        search: search || undefined, sort_by: sortBy || undefined, sort_dir: sortDir || undefined,
        academic_term_id: currentTermId || undefined,
        class_id: filterClassId || undefined,
        level_id: filterLevelId || undefined,
        status: filterStatus || undefined,
    }), [search, sortBy, sortDir, currentTermId, filterClassId, filterLevelId, filterStatus]);

    const { isLoading, error, enrollments, meta } = useGetStudentEnrollments(page, perPage, filters);

    const renderRow = (params) => (
        <StudentEnrollmentsTableRow key={params.key || params.item.id} item={params.item} index={params.index}
            isArabic={params.isArabic} isDark={params.isDark} page={params.page} perPage={params.perPage}
            order={params.order} totalItems={enrollments.length} selectedIds={[]}
            onRowCheckboxClick={() => {}} onMenuOpen={params.onMenuOpen} />
    );

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2, flexWrap: 'wrap' }}>
                <TextField select size="small" value={filterClassId}
                    onChange={(e) => setFilterClassId(e.target.value)}
                    label={isArabic ? 'الحلقة' : 'Class'}
                    dir={isArabic ? 'rtl' : 'ltr'}
                    sx={{ minWidth: 180, ...rtlSelectSx(isArabic) }}
                    SelectProps={{ MenuProps: { PaperProps: { sx: { direction: isArabic ? 'rtl' : 'ltr', maxHeight: 300 } } } }}>
                    <MenuItem value="">{isArabic ? 'الكل' : 'All'}</MenuItem>
                    {(classes || []).map((c) => (<MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>))}
                </TextField>

                <TextField select size="small" value={filterLevelId}
                    onChange={(e) => setFilterLevelId(e.target.value)}
                    label={isArabic ? 'المستوى' : 'Level'}
                    dir={isArabic ? 'rtl' : 'ltr'}
                    sx={{ minWidth: 160, ...rtlSelectSx(isArabic) }}
                    SelectProps={{ MenuProps: { PaperProps: { sx: { direction: isArabic ? 'rtl' : 'ltr', maxHeight: 300 } } } }}>
                    <MenuItem value="">{isArabic ? 'الكل' : 'All'}</MenuItem>
                    {(levels || []).map((l) => (<MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>))}
                </TextField>

                <TextField select size="small" value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    label={isArabic ? 'الحالة' : 'Status'}
                    dir={isArabic ? 'rtl' : 'ltr'}
                    sx={{ minWidth: 150, ...rtlSelectSx(isArabic) }}
                    SelectProps={{ MenuProps: { PaperProps: { sx: { direction: isArabic ? 'rtl' : 'ltr' } } } }}>
                    <MenuItem value="">{isArabic ? 'الكل' : 'All'}</MenuItem>
                    <MenuItem value="active">{isArabic ? 'نشط' : 'Active'}</MenuItem>
                    <MenuItem value="transferred">{isArabic ? 'منقول' : 'Transferred'}</MenuItem>
                    <MenuItem value="completed">{isArabic ? 'مكتمل' : 'Completed'}</MenuItem>
                    <MenuItem value="cancelled">{isArabic ? 'ملغي' : 'Cancelled'}</MenuItem>
                </TextField>
            </Box>

            <TableLayout
                title={isArabic ? 'تسجيلات الطلاب' : 'Student Enrollments'}
                subtitle={isArabic ? 'سجل تسجيلات الطلاب في الحلقات' : 'Student class enrollment history'}
                search={search} onSearchChange={setSearch}
                addButtonLabel={isArabic ? 'تسجيل جديد' : 'New Enrollment'}
                onAddClick={() => navigate('/student-enrollments/create')} addButtonVisible={perms.create}
                isLoading={isLoading} error={error} items={enrollments} meta={meta}
                page={page} perPage={perPage} onPageChange={handlePageChange} onPerPageChange={handlePerPageChange}
                sortBy={sortBy} sortDir={sortDir} onRequestSort={handleRequestSort}
                selectedIds={[]} onSelectAllClick={() => {}} onRowCheckboxClick={() => {}}
                hideCheckboxes={true}
                columns={StudentEnrollmentsTableColumns} renderRow={renderRow}
                menuAnchorEl={menuAnchorEl} openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen} onMenuClose={handleMenuClose}
                onMenuEdit={perms.edit ? handleEdit : undefined}
                onMenuDelete={perms.delete ? handleMenuDelete : undefined}
                menuDisableEdit={!perms.edit} menuDisableDelete={!perms.delete}
                isDeletingSingle={isDelete}
                emptyLabelAr="لا توجد تسجيلات" emptyLabelEn="No enrollments"
                loadErrorAr="حدث خطأ أثناء تحميل التسجيلات" loadErrorEn="Failed to load enrollments"
            />
        </>
    );
};

export default StudentEnrollmentsTable;
